import { ReactPlayerErrorBoundaryRetry } from "@package/ui-apollo-components";
import { EventPayloadType } from "@package/ui-gql/index-analytics";
import { useWallAnalytics, useWallProvider } from "@package/ui-wall";
import { get, map } from "lodash";
import { useCallback, useEffect, useMemo } from "react";
import { type FlyoutProps } from "../../utils/flyout-props";

const KEEP_ALIVE_INTERVAL = 1000;

export function FlyoutVideoGallery({ screen }: FlyoutProps) {
  const provider = useWallProvider();
  const { trigger } = useWallAnalytics();

  const urls = useMemo<string[]>(
    () =>
      map(
        screen.flyout?.post ? get(screen.flyout.post, "customData.videos") : [],
        "url",
      ).filter((url) => !!url),
    [screen.flyout?.post],
  );

  const onHandleMouse = useCallback(
    (x: number, y: number) => {
      provider?.onFlyoutClosed(screen.id);
      trigger(
        EventPayloadType.PostClosed,
        {
          position: {
            x,
            y,
          },
          screen: screen.index,
        },
        screen.flyout?.post?.id ?? undefined,
        screen.layout?.id ?? undefined,
      );
    },
    [
      provider,
      screen.flyout?.post?.id,
      screen.id,
      screen.index,
      screen.layout?.id,
      trigger,
    ],
  );

  useEffect(() => {
    const interval = setInterval(() => {
      if (screen.flyout?.post) {
        provider?.onFlyoutKeepAlive(screen.id, screen.flyout.post);
      }
    }, KEEP_ALIVE_INTERVAL);

    return () => clearInterval(interval);
  }, [provider, screen.flyout?.post, screen.id]);

  return (
    <div className="relative h-full w-full">
      <ReactPlayerErrorBoundaryRetry
        width="100%"
        height="100%"
        loop={false}
        muted
        playing
        pip={false}
        url={urls}
        onEnded={() => {
          provider?.onFlyoutClosed(screen.id);
          trigger(
            EventPayloadType.PostTimedOut,
            {
              screen: screen.index,
            },
            screen.flyout?.post?.id ?? undefined,
            screen.layout?.id ?? undefined,
          );
        }}
        onErrorEvent={(error) => {
          provider?.onFlyoutClosed(screen.id);
          console.error("[VideoGallery]: Video player error:", error);
        }}
        onMediaStalled={() => {
          provider?.onFlyoutClosed(screen.id);
          console.error(
            "[VideoGallery]: Video player stalled, closing flyout.",
          );
        }}
      />
      <div
        className="absolute top-0 left-0 w-full h-full"
        onPointerDown={(e) => onHandleMouse(e.screenX, e.screenY)}
      />
    </div>
  );
}
